/**
 * @author: chenlv
 * @date: 2025/01/22
 * @description: Genre Share 品类份额卡片组件（饼状图）
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import NoData from '@intelligence/components/NoData';
import ChartCard from '@intelligence/v3/components/ChartCard';
import { PieChart } from '@intelligence/v3/components/ChartComponents';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { isEmpty } from '@intelligence/v3/utils/common';
import { getBarOrPieData } from './utils';
import { createPieChartTooltipFormatter } from './tooltipFormatters';
import { ChartType } from '@intelligence/v3/enums/chart';
import { METRIC_NAME_I18N_KEY_MAP, PIE_CHART_CENTER_TITLE_CONFIGS } from '../../constants';

import type { IChartCardBaseProps } from './types';

/**
 * GenreShareCard 组件 Props
 */
interface IGenreShareCardProps extends IChartCardBaseProps {}

/**
 * GenreShareCard 品类份额卡片组件
 * @description 展示当前选中指标的品类份额饼状图
 */
const GenreShareCard: React.FC<IGenreShareCardProps> = (props) => {
  const {
    style,
    className,
    loading = false,
    genreSource,
    genreDimension,
    dataSource,
    metric,
    list,
    formatGenreName,
    labelFormatOfPieChart,
    onPieClick,
  } = props;

  const { locale, formatMessage } = useIntlExtend();

  /**
   * 获取卡片标题
   */
  const cardTitle = useMemo(() => {
    const titleKeyMap: Record<IGenreSource, string> = {
      iegg: '',
      sensortower: 'dbi_casual_genre_share_main_genre',
      annie: '',
      appmagic: 'dbi_casual_genre_share_super_genre',
    };
    const titleKey = titleKeyMap[genreSource] || '';
    return formatMessage({ id: titleKey });
  }, [genreSource, formatMessage]);

  /**
   * 获取指标名称
   */
  const metricName = useMemo(() => {
    const metricKey = METRIC_NAME_I18N_KEY_MAP[metric] || '';
    return formatMessage({ id: metricKey });
  }, [locale, metric]);

  /**
   * 创建饼状图 tooltip formatter
   */
  const pieTooltipFormatter = useMemo(() => {
    return createPieChartTooltipFormatter({
      dataSource,
      metric,
      metricName,
      formatGenreName,
    });
  }, [dataSource, metric, metricName, formatGenreName]);

  /**
   * 获取饼状图数据
   */
  const pieData = useMemo<IPieChartData[]>(() => {
    if (loading) {
      return [];
    }

    return getBarOrPieData({
      genreSource,
      genreDimension,
      dataSource,
      metric,
      list,
    });
  }, [loading, genreSource, genreDimension, dataSource, metric, list]);

  /**
   * 是否无数据
   */
  const noData = useMemo<boolean>(() => {
    if (loading) {
      return false;
    }

    return isEmpty(pieData);
  }, [loading, pieData]);

  return (
    <ChartCard
      style={style}
      className={classnames('dbi-genre-share-card', className)}
      loading={loading}
      title={cardTitle}
      genreSource={genreSource}
      chartTypes={[ChartType.Pie]}
      chartType={ChartType.Pie}
    >
      {noData ? (
        <div className="dbi-genre-share-card__no-data">
          <NoData content={formatMessage({ id: 'common_nodata' })} />
        </div>
      ) : (
        <PieChart
          showLabel
          showLegend
          locale={locale as any}
          loading={loading}
          centerTitle={formatMessage({ id: 'dbi_casual_genre_breakdown' })}
          centerTitleConfigs={PIE_CHART_CENTER_TITLE_CONFIGS}
          data={pieData}
          labelFormatter={labelFormatOfPieChart}
          legendFormatter={formatGenreName}
          tooltipFormatter={pieTooltipFormatter}
          onPieClick={onPieClick}
        />
      )}
    </ChartCard>
  );
};

export default GenreShareCard;
